import * as React from 'react';
import { StaffRepository, UserRepository } from '../../MedicareProAPI';
import type { IpList } from '../../MedicareProAPI/Entities/IpList';

type IpRow = {
  id: number;
  ip: string;
};

type IpSubjectType = 'Agent' | 'Staff';

type UserIpWhitelistRowProps = {
  userAccessToken: string;
  subjectType: IpSubjectType;
  subjectId: number;
  subjectName?: string;
  initialIps?: any;
};

/**
 * Management for single IP for user/staff
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-12-03
 * @lastUpdated
 * @version 1.1.0
 */
export const UserIpWhitelistRow = ({
  userAccessToken,
  subjectType,
  subjectId,
  subjectName,
  initialIps,
}: UserIpWhitelistRowProps) => {
  const [ips, setIps] = React.useState(initialIps || []);
  const [addInput, setAddInput] = React.useState<string>('');
  const [error, setError] = React.useState<string | null>(null);
  const [isAdding, setIsAdding] = React.useState<boolean>(false);

  const byId = (id: number) => (x) => (x.getId?.() ?? (x as any).id) === id;
  const toId = (x) => x.getId?.() ?? (x as any).id;
  const toIp = (x) => x.getIp?.() ?? (x as any).ip ?? '';
  const toDisabled = (x) => x.getDisabled?.() ?? (x as any).disabled ?? 0;

  const userRepository = React.useMemo(() => {
    const r = new UserRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const staffRepository = React.useMemo(() => {
    const r = new StaffRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  /**
   * Add a new IP to the subject's whitelist.
   *
   * Performs an optimistic insert into local state and
   * rolls back if the backend call fails.
   */
  const addIp = async (): Promise<void> => {
    const ip = addInput.trim();
    if (!ip) return;

    // Optimistic stub entity
    const tempId = -Date.now();
    const optimistic = {
      getId: () => tempId,
      getIp: () => ip,
      setIp: (_: string) => undefined,
    };

    setIps((list) => [...list, optimistic]);
    setAddInput('');
    setError(null);
    setIsAdding(false);
    try {
      let created: IpList;

      if (subjectType === 'Agent') {
        created = await userRepository.addUserIp(subjectId, { ip });
      } else {
        created = await staffRepository.addStaffIp(subjectId, { ip });
      }

      setIps((list) => list.map((x) => (byId(tempId)(x) ? created : x)));
    } catch (e: unknown) {
      setIps((list) => list.filter((x) => !byId(tempId)(x)));
      const message = e instanceof Error ? e.message : String(e ?? 'Unknown error');
      setError(message);
    }
  };

  /**
   * Remove an IP from the subject's whitelist.
   *
   * Performs an optimistic update of local state and reverts
   * if the backend deletion fails.
   *
   * @param id - The ID of the IP entry to remove.
   */
  const removeIp = async (id: number): Promise<void> => {
    let prev: IpRow[] = [];

    setIps((list) => {
      prev = list;
      return list.filter((x) => !byId(id)(x));
    });

    try {
      if (subjectType === 'Agent') {
        await userRepository.removeUserIp(subjectId, id);
      } else {
        await staffRepository.removeStaffIp(subjectId, id);
      }
    } catch (e: unknown) {
      setIps(prev);
      const message = e instanceof Error ? e.message : String(e ?? 'Unknown error');
      setError(message);
    }
  };

  const subjectClass = ['badge', 'ml-2'];
  subjectType === 'Staff' ? subjectClass.push('badge-danger') : subjectClass.push('badge-info');

  return (
    <div className="row border-bottom mx-0 py-2">
      {subjectName && (
        <div className="col-12 d-flex align-items-center mb-2">
          <div className="font-weight-bold">
            {subjectName} <span className={subjectClass.join(' ')}>{subjectType}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="col-12">
          <div className="alert alert-danger py-1 mb-2 small">{error}</div>
        </div>
      )}

      <div className="col-12">
        {ips.length ? (
          <div className="mb-2">
            {ips.map((item) => {
              const id = toId(item);
              const ip = toIp(item);
              const disabled = !!toDisabled(item);

              return (
                <div key={id} className="d-flex align-items-center mb-1">
                  <span className="dataBadge font-weight-bold mr-2 rounded p-1 px-2 bg-contrast text-success">
                    {ip}
                  </span>
                  <button className="btn btn-sm btn-link text-danger" onClick={() => removeIp(id)}>
                    Delete
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="small text-muted mb-2">No IPs for this {subjectType}.</div>
        )}

        <div className="d-flex align-items-center">
          {!isAdding && (
            <div>
              <i
                className="fas fa-plus-circle text-primary cp"
                onClick={() => setIsAdding(true)}
                style={{ fontSize: '14pt' }}
              ></i>
            </div>
          )}
          {isAdding && (
            <>
              <input
                type="text"
                className="form-control form-control-sm mr-2"
                placeholder="Add IP (e.g., 203.0.113.42)"
                value={addInput}
                onChange={(e) => setAddInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addIp();
                }}
                style={{ maxWidth: 260 }}
              />
              <button className="btn btn-sm btn-primary" onClick={addIp}>
                Add
              </button>
              <button
                className="btn btn-sm btn-link text-secondary"
                onClick={() => {
                  setIsAdding(false);
                  setAddInput('');
                }}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
